import { useEffect, useRef } from 'react';
import { useFeedbackMarkup } from './FeedbackMarkupProvider.jsx';

export function RegionSelector() {
  const { state, dispatch, captureRegion } = useFeedbackMarkup();
  const startRef = useRef(null);
  const regionRef = useRef(state.regionBox);

  useEffect(() => {
    regionRef.current = state.regionBox;
  }, [state.regionBox]);

  useEffect(() => {
    if (!state.regionSelecting) return undefined;
    const cancel = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      startRef.current = null;
      regionRef.current = null;
      dispatch({ type: 'region/cancel' });
    };
    window.addEventListener('keydown', cancel, true);
    return () => window.removeEventListener('keydown', cancel, true);
  }, [dispatch, state.regionSelecting]);

  if (!state.regionSelecting) return null;

  function update(clientX, clientY) {
    const start = startRef.current;
    if (!start) return;
    const region = {
      x: Math.min(start.x, clientX),
      y: Math.min(start.y, clientY),
      width: Math.abs(clientX - start.x),
      height: Math.abs(clientY - start.y),
    };
    regionRef.current = region;
    dispatch({
      type: 'region/update',
      region,
    });
  }

  function cancelPointerSelection() {
    startRef.current = null;
    regionRef.current = null;
    dispatch({ type: 'region/cancel' });
  }

  return (
    <div
      className={`feedback-region-layer${state.regionBox ? ' is-selecting' : ''}`}
      data-testid="feedback-region-layer"
      role="application"
      aria-label="Select feedback capture region"
      aria-describedby="feedback-region-instructions"
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture?.(event.pointerId);
        startRef.current = { x: event.clientX, y: event.clientY };
        update(event.clientX, event.clientY);
      }}
      onPointerMove={(event) => {
        update(event.clientX, event.clientY);
      }}
      onPointerUp={() => {
        const region = regionRef.current;
        startRef.current = null;
        regionRef.current = null;
        dispatch({ type: 'region/cancel' });
        captureRegion(region);
      }}
      onPointerCancel={cancelPointerSelection}
    >
      <span id="feedback-region-instructions" className="sr-only">
        Drag to select a capture region. Touch dragging is supported. Press Escape to cancel.
      </span>
      {state.regionBox ? (
        <div
          className="feedback-region-box"
          data-testid="feedback-region-box"
          style={{
            left: state.regionBox.x,
            top: state.regionBox.y,
            width: state.regionBox.width,
            height: state.regionBox.height,
          }}
        >
          <span>
            {Math.round(state.regionBox.width)} × {Math.round(state.regionBox.height)}
          </span>
        </div>
      ) : (
        <div className="feedback-region-hint" data-testid="feedback-region-hint">
          Drag to select the part of the screen to capture — press Esc to cancel
        </div>
      )}
    </div>
  );
}
